import { Signal } from '@preact/signals-core';
import { batch, computed, ReadonlySignal, signal } from '@preact/signals-react';
import { injectable } from 'inversify';

import { Failure, OperationResult, Success } from '@vp/common/core/OperationResult';
import { ViewModel, ViewModelDispose } from '@vp/common/ui/ViewModel';
import { sleep } from '@vp/common/util';
import { ProfilePort } from '@vp/profile/core/interface/ProfilePort';
import { ProfileAccessModel } from '@vp/profile/core/model/ProfileAccessModel';
import { ProfileModel } from '@vp/profile/core/model/ProfileModel';
import { ProfileAccessProps } from '@vp/profile/ui/access/ProfileAccess';

type ProfileAccessStatus = 'success' | 'error' | 'regular' | 'loading';

@injectable()
export class ProfileAccessViewModel extends ViewModel<ProfileAccessProps> implements ViewModelDispose {
  readonly code: Signal<string> = signal('');
  readonly errorMessage: Signal<string> = signal('');
  readonly status: ReadonlySignal<ProfileAccessStatus> = computed(() => this.defineStatus());

  private readonly internalStatus: Signal<ProfileAccessStatus> = signal('regular');

  private controller?: AbortController;

  constructor(private readonly profilePort: ProfilePort) {
    super();
  }

  dispose(): void {
    this.controller?.abort();
  }

  onCodeChange = async (code: string): Promise<void> => {
    this.controller?.abort();

    batch(() => {
      this.internalStatus.value = 'regular';
      this.errorMessage.value = '';
      this.code.value = code;
    });

    if (code.length !== ProfileAccessModel.codeLength) return;

    this.controller = new AbortController();
    this.internalStatus.value = 'loading';
    await sleep(1000);

    const id = this.profilePort.profile.value.id;
    const result = await this.profilePort.unlockProfile(id, this.code.value, this.controller);

    await this.handleResult(result);
  };

  private async handleResult(result: OperationResult<ProfileModel>): Promise<void> {
    if (result instanceof Success) {
      await this.handleSuccess(result.value);
    } else {
      this.handleFailure(result);
    }
  }

  private async handleSuccess(profile: ProfileModel): Promise<void> {
    if (this.props.value?.onProfileUnlock) {
      this.props.value?.onProfileUnlock?.(profile);
    } else {
      this.internalStatus.value = 'success';
      await sleep(1000);
      await this.profilePort.setProfile(profile);
    }
  }

  private handleFailure(failure: Failure): void {
    batch(() => {
      this.internalStatus.value = 'error';
      this.errorMessage.value = failure.message || 'Неверный код';
    });
  }

  private defineStatus(): ProfileAccessStatus {
    if (this.props.value?.isUnlocked) {
      return 'success';
    }

    return this.internalStatus.value;
  }
}
